import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { memo, useMemo } from 'react';
import { JsonLd } from 'react-schemaorg';
import type { WithContext, SportsEvent } from 'schema-dts';

import { withErrorBoundary } from '../ErrorBoundary';
import { getCompetitionStructureQuery } from '../queries/competition';
import { useTranslateFn } from '../Translate';

import useMatch from './useMatch';

import { getCompetitionUrl, getMatchUrl, getTeamUrl } from '@services/LinkRuleService';
import { hydrateWithTranslations } from '@services/TranslatorService';
import type { Match } from '@shared-types/match.types';
import { MatchStatus } from '@shared-types/match.types';
import { orderBy } from '@utilities/collection-utils';
import { matchScore, matchLink, getSeasonLabelByMatch } from '@utilities/live';

const statusMap = {
  [MatchStatus.CANCELED]: 'EventCancelled',
  [MatchStatus.POSTPONED]: 'EventPostponed',
  [MatchStatus.UPCOMING]: 'EventScheduled',
} as const;

const MatchJsonLd = ({ match: originalMatch }: { match: Match }) => {
  const match = useMatch(originalMatch);
  const t = useTranslateFn();
  const competition = useMemo(() => hydrateWithTranslations(match.competition), [match.competition]);

  const { data: rounds } = useQuery({
    ...getCompetitionStructureQuery({
      competitionId: competition?.id,
      seasonYears: match.seasonYear ?? 'current',
      phase: 'ALL',
    }),
    select: (data) => orderBy(data?.rounds, 'orderInCompetition'),
    enabled: competition?.region !== 'DOMESTIC' && !!competition?.id,
  });

  const homeTeam = useMemo(() => hydrateWithTranslations(match.homeTeam), [match.homeTeam]);
  const awayTeam = useMemo(() => hydrateWithTranslations(match.awayTeam), [match.awayTeam]);
  const stadium = useMemo(() => hydrateWithTranslations(match.stadium), [match.stadium]);
  const homeScore = useMemo(() => matchScore(match, 'home'), [match]);
  const awayScore = useMemo(() => matchScore(match, 'away'), [match]);
  const homeaway = `${homeTeam?.displayName} ${homeScore ?? ''}-${awayScore ?? ''} ${awayTeam?.displayName}`;
  const matchUrl = useMemo(() => matchLink(getMatchUrl(match.id)), [match.id]);
  const eventStatus = statusMap[match.status] ?? 'EventMovedOnline';

  const seasonLabel = useMemo(() => getSeasonLabelByMatch(match), [match]);

  const matchDate = useMemo(() => match.kickOffTime?.dateTime ?? match.kickOffTime?.date, [match]);
  const startDate = useMemo(() => (matchDate ? dayjs(matchDate).toISOString() : ''), [matchDate]);
  const endDate = useMemo(() => {
    if (match.fullTimeAt) return dayjs(match.fullTimeAt).toISOString();

    if (matchDate) return dayjs(matchDate).add(3, 'hours').toISOString();

    return '';
  }, [match.fullTimeAt, matchDate]);

  const competitionStartDate = useMemo(() => {
    const roundDate = rounds?.at(0)?.dateFrom;
    return roundDate && dayjs(roundDate)?.toISOString();
  }, [rounds]);

  const competitionEndDate = useMemo(() => {
    const roundDate = rounds?.filter((r) => r?.dateTo).at(-1)?.dateTo;
    return roundDate && dayjs(roundDate)?.toISOString();
  }, [rounds]);

  const translatedDescr = t(`og-description-${window.competitionFolder}`);

  const matchData: WithContext<SportsEvent> = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'SportsEvent',
      url: matchUrl,
      name: homeaway,
      eventAttendanceMode: 'MixedEventAttendanceMode',
      description: `${[competition?.name, seasonLabel, hydrateWithTranslations(match.round)?.name]
        .filter(Boolean)
        .join(' ')}: ${[homeTeam?.displayName, awayTeam?.displayName].filter(Boolean).join('-')}`,
      eventStatus,
      startDate,
      endDate,
      organizer: {
        '@type': 'Organization',
        url: location.origin,
        name: 'UEFA',
      },
      superEvent: competitionStartDate && {
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        name: `${competition?.name} ${seasonLabel}`,
        description: translatedDescr,
        image:
          competition && `${window.imgBaseUrl}/imgml/uefacom/elements/logos/competitions/pngs/${competition.id}.png`,
        eventStatus: 'EventMovedOnline',
        eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
        url: competition && getCompetitionUrl(competition.id),
        organizer: {
          '@type': 'Organization',
          url: location.origin,
          name: 'UEFA',
        },
        location: {
          '@type': 'VirtualLocation',
          url: competition && getCompetitionUrl(competition.id),
        },
        startDate: competitionStartDate,
        endDate: competitionEndDate,
      },
      location: [
        {
          '@type': 'VirtualLocation',
          url: matchUrl,
        },
        {
          '@type': 'StadiumOrArena',
          name: stadium ? stadium.name : 'TBD',
          image: stadium && `${window.imgBaseUrl}/imgml/stadium/w1/${stadium.id}.jpg`,
          address: {
            '@type': 'PostalAddress',
            addressLocality: hydrateWithTranslations(stadium?.city)?.name,
            addressCountry: stadium?.countryCode,
          },
        },
      ],
      awayTeam: {
        '@type': 'SportsTeam',
        name: awayTeam?.displayName,
        logo: awayTeam?.LogoUrl,
        sameAs: getTeamUrl(awayTeam?.id, undefined, undefined, match?.round?.phase),
      },
      homeTeam: {
        '@type': 'SportsTeam',
        name: homeTeam?.displayName,
        logo: homeTeam?.LogoUrl,
        sameAs: getTeamUrl(homeTeam?.id, undefined, undefined, match?.round?.phase),
      },
      offers: {
        '@type': 'Offer',
        url: 'https://store.uefa.com/',
        priceCurrency: 'EUR',
      },
      performer: [
        {
          '@type': 'SportsTeam',
          name: homeTeam?.displayName,
          sameAs: getTeamUrl(homeTeam?.id, undefined, undefined, match?.round?.phase),
        },

        {
          '@type': 'SportsTeam',
          name: awayTeam?.displayName,
          sameAs: getTeamUrl(awayTeam?.id, undefined, undefined, match?.round?.phase),
        },
      ],
    }),
    [
      awayTeam?.LogoUrl,
      awayTeam?.displayName,
      awayTeam?.id,
      competition,
      competitionEndDate,
      competitionStartDate,
      startDate,
      endDate,
      eventStatus,
      homeTeam?.LogoUrl,
      homeTeam?.displayName,
      homeTeam?.id,
      homeaway,
      match.round,
      matchUrl,
      seasonLabel,
      stadium,
      translatedDescr,
    ],
  );

  return <JsonLd item={matchData} />;
};

export default withErrorBoundary(memo(MatchJsonLd));
